import { z } from 'zod';
/**
 * Vender pelo Secretário, fase 1 (estudo "Vender", 23/10/2026): a loja monta a vitrine, recebe
 * o pedido e o cliente paga no Pix da própria loja, com o código gerado no app. O Secretário
 * não toca no dinheiro e não cobra por venda. A loja confirma o recebimento.
 */
export const PIX_KEY_TYPES = ['cpf', 'cnpj', 'email', 'telefone', 'aleatoria'];
export const pixKeyTypeSchema = z.enum(PIX_KEY_TYPES);
export const PIX_KEY_TYPE_LABEL = {
    cpf: 'CPF',
    cnpj: 'CNPJ',
    email: 'E-mail',
    telefone: 'Celular',
    aleatoria: 'Chave aleatória',
};
/** O que não se vende pelo Secretário (lei, risco ou regra das lojas de aplicativo). */
export const SHOP_BLOCKED = [
    { key: 'farmacia', label: 'Remédios e farmácia' },
    { key: 'tabaco', label: 'Cigarro, vape e tabaco' },
    { key: 'armas', label: 'Armas e munição' },
    { key: 'adulto', label: 'Conteúdo adulto' },
];
const money = z.number().int().min(0).max(10_000_000);
/** Uma escolha dentro de uma opção ("Grande", +R$ 12,00). */
export const shopChoiceSchema = z.object({
    name: z.string().trim().min(1).max(40),
    deltaCents: z.number().int().min(-1_000_000).max(1_000_000),
});
/** Uma opção do produto: tamanho, sabor, borda. `max` > 1 deixa escolher mais de um. */
export const shopOptionSchema = z.object({
    name: z.string().trim().min(1).max(40),
    required: z.boolean(),
    max: z.number().int().min(1).max(10),
    choices: z.array(shopChoiceSchema).min(1).max(20),
});
export const shopProductInputSchema = z.object({
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(400).nullable(),
    priceCents: money.min(1),
    /** A prateleira da vitrine ("Pizzas", "Bebidas"). */
    section: z.string().trim().max(40).nullable(),
    options: z.array(shopOptionSchema).max(6),
    /** Quantos há; nulo quando não controla estoque. */
    stock: z.number().int().min(0).max(100_000).nullable(),
    active: z.boolean(),
});
export const shopProductSchema = shopProductInputSchema.extend({
    id: z.uuid(),
    /** Endereço da foto (versão no fim, para o cache), ou nulo. */
    photo: z.string().nullable(),
    sort: z.number().int(),
});
/** Os dados da loja que o dono preenche. O que é exigência do Decreto 7.962 vem marcado. */
export const shopSettingsInputSchema = z.object({
    /** Recebendo pedidos agora (a loja pode pausar sem sumir da vitrine). */
    open: z.boolean(),
    /** Decreto 7.962: nome empresarial ou nome completo de quem vende. */
    legalName: z.string().trim().min(3).max(100),
    /** Decreto 7.962: CNPJ ou CPF de quem vende (só números). */
    document: z.string().regex(/^(\d{11}|\d{14})$/),
    /** Decreto 7.962: endereço físico. */
    address: z.string().trim().min(5).max(160),
    city: z.string().trim().min(2).max(60),
    pixKeyType: pixKeyTypeSchema,
    pixKey: z.string().trim().min(3).max(77),
    pickup: z.boolean(),
    delivery: z.boolean(),
    deliveryFeeCents: money,
    /** Até onde entrega, em km (só informativo na fase 1). */
    deliveryRadiusKm: z.number().min(0).max(100),
    minOrderCents: money,
    /** Tempo médio até ficar pronto ou sair, em minutos. */
    prepMinutes: z.number().int().min(5).max(600),
    /** Troca, devolução e arrependimento (CDC art. 49 vale para compra fora da loja). */
    policy: z.string().trim().max(800).nullable(),
    /** O dono declara que não vende nada da lista proibida. */
    declaresAllowed: z.literal(true),
});
export const shopSettingsSchema = shopSettingsInputSchema
    .omit({ declaresAllowed: true })
    .extend({ updatedAt: z.string() });
/** O estado do lojista: sem loja ainda, ou a loja com os ajustes e a vitrine. */
export const shopOwnerSchema = z.object({
    settings: shopSettingsSchema.nullable(),
    products: z.array(shopProductSchema),
    /** Endereço público da vitrine (o link da loja). */
    link: z.string().nullable(),
});
// ── Pedido ─────────────────────────────────────────────────────────────────────
export const SHOP_ORDER_STATUSES = [
    'novo',
    'aceito',
    'pronto',
    'saiu',
    'concluido',
    'recusado',
    'cancelado',
];
export const shopOrderStatusSchema = z.enum(SHOP_ORDER_STATUSES);
export const SHOP_ORDER_STATUS_LABEL = {
    novo: 'Novo',
    aceito: 'Preparando',
    pronto: 'Pronto para retirar',
    saiu: 'Saiu para entrega',
    concluido: 'Concluído',
    recusado: 'Recusado',
    cancelado: 'Cancelado',
};
export const SHOP_PAYMENT_STATES = ['pendente', 'informado', 'confirmado', 'devolvido'];
export const shopPaymentStateSchema = z.enum(SHOP_PAYMENT_STATES);
export const FULFILLMENTS = ['retirada', 'entrega'];
export const fulfillmentSchema = z.enum(FULFILLMENTS);
/** O que o cliente pôs na sacola: o produto, quantas e o índice das escolhas de cada opção. */
export const shopCartLineSchema = z.object({
    productId: z.uuid(),
    qty: z.number().int().min(1).max(50),
    choices: z.array(z.array(z.number().int().min(0).max(19)).max(10)).max(6),
});
export const shopCheckoutSchema = z.object({
    lines: z.array(shopCartLineSchema).min(1).max(40),
    fulfillment: fulfillmentSchema,
    address: z.string().trim().max(200).nullable(),
    note: z.string().trim().max(300).nullable(),
});
export const shopOrderLineSchema = z.object({
    productId: z.uuid(),
    name: z.string(),
    qty: z.number().int(),
    /** As escolhas por extenso ("Grande", "Meia calabresa"). */
    choices: z.array(z.string()),
    unitCents: z.number().int(),
    lineCents: z.number().int(),
});
export const shopOrderSchema = z.object({
    id: z.uuid(),
    /** Número curto para falar ao telefone ("7F3A"). */
    code: z.string(),
    shop: z.object({ username: z.string(), name: z.string() }),
    customer: z.object({ name: z.string(), username: z.string() }),
    lines: z.array(shopOrderLineSchema),
    subtotalCents: z.number().int(),
    deliveryCents: z.number().int(),
    totalCents: z.number().int(),
    fulfillment: fulfillmentSchema,
    address: z.string().nullable(),
    note: z.string().nullable(),
    status: shopOrderStatusSchema,
    payment: shopPaymentStateSchema,
    /** O Pix copia e cola deste pedido (com a chave da loja e o valor). */
    pix: z.string(),
    /** Para quem paga conferir no banco: o nome e a chave mascarada. */
    pixName: z.string(),
    pixKeyHint: z.string(),
    reason: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** O que o lojista faz com o pedido. */
export const SHOP_ORDER_ACTIONS = [
    'aceitar',
    'recusar',
    'pronto',
    'saiu',
    'concluir',
    'recebi',
    'devolvi',
];
export const shopOrderActionSchema = z.object({
    action: z.enum(SHOP_ORDER_ACTIONS),
    reason: z.string().trim().max(200).optional(),
});
/** O que o cliente faz: avisar que pagou ou desistir (só antes de a loja aceitar). */
export const shopCustomerActionSchema = z.object({ action: z.enum(['paguei', 'desistir']) });
// ── Vitrine pública ────────────────────────────────────────────────────────────
export const shopStorefrontSchema = z.object({
    username: z.string(),
    name: z.string(),
    about: z.string().nullable(),
    open: z.boolean(),
    pickup: z.boolean(),
    delivery: z.boolean(),
    deliveryFeeCents: z.number().int(),
    deliveryRadiusKm: z.number(),
    minOrderCents: z.number().int(),
    prepMinutes: z.number().int(),
    policy: z.string().nullable(),
    /** Decreto 7.962: quem vende, documento e endereço, à vista na vitrine. */
    seller: z.object({
        legalName: z.string(),
        document: z.string(),
        address: z.string(),
        city: z.string(),
    }),
    products: z.array(shopProductSchema.omit({ active: true })),
});
// ── Financeiro do lojista ──────────────────────────────────────────────────────
export const shopFinanceSchema = z.object({
    /** Mês consultado (AAAA-MM). */
    month: z.string(),
    /** Pedidos que valem (sem recusados e cancelados). */
    orders: z.number().int(),
    /** Recebido: a loja confirmou o Pix. */
    receivedCents: z.number().int(),
    /** O cliente disse que pagou e a loja ainda não conferiu. */
    toCheckCents: z.number().int(),
    /** Sem pagamento ainda. */
    pendingCents: z.number().int(),
    averageCents: z.number().int(),
    days: z.array(z.object({ day: z.string(), orders: z.number().int(), receivedCents: z.number().int() })),
    top: z.array(z.object({ name: z.string(), qty: z.number().int(), cents: z.number().int() })),
});
